/**
 * @CODE:SPEC-ACC-002-PAGE-001
 * AccommodationSearch Page Component
 *
 * Main page for accommodation search featuring:
 * - Responsive layout (mobile: 1 column, tablet: 2 columns, desktop: 3 columns)
 * - AccommodationFilters component for search filtering
 * - AccommodationList component for displaying results
 * - Pagination support (20 items per page)
 * - Mobile sheet modal for filters
 * - Scroll-to-top on page change
 * - Framer Motion page transitions
 *
 * @performance
 * - Uses React.useMemo for filter counts to avoid unnecessary recalculations
 * - Uses React.useCallback for event handlers to prevent unnecessary re-renders
 * - Implements smooth scroll-to-top behavior on pagination
 * - Lazy loads components with proper error boundaries
 */

import { useState, useCallback, useMemo, useRef } from "react";
import { Filter, ChevronDown, X, Loader2 } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import TrailSelector from "@/features/accommodation/components/TrailSelector";
import AccommodationFilters from "@/features/accommodation/components/AccommodationFilters";
import AccommodationList from "@/features/accommodation/components/AccommodationList";
import PaginationControls from "@/features/accommodation/components/PaginationControls";
import {
  useAccommodationSearch,
  useCoursesForTrail,
} from "@/features/accommodation/hooks";
import { useIsMobile } from "@/hooks/use-mobile";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger, SheetFooter } from "@/components/ui/sheet";
import type { AccommodationFilters as AccommodationFiltersType } from "@/features/accommodation/types";
import { ACCOMMODATION_TYPES } from "@/features/accommodation/constants/accommodation-types";

const AccommodationSearch = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedTrailId, setSelectedTrailId] = useState<string>("T_THEME_MNG0000011235"); // Default: 해파랑길
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const [tabletFiltersCollapsed, setTabletFiltersCollapsed] = useState(false);
  const trailSelectorRef = useRef<HTMLDivElement>(null);
  const itemsPerPage = 20;
  const isMobile = useIsMobile();

  // Use the accommodation search hook
  const {
    accommodations,
    total,
    isLoading,
    isError,
    error,
    filters,
    totalPages: apiTotalPages,
    updateFilters,
    goToPage,
  } = useAccommodationSearch({
    enabled: true,
    initialFilters: {
      limit: itemsPerPage,
      page: currentPage,
      trail_id: selectedTrailId,
      distance_from_course_km: 10, // Default: 10km
    },
  });

  // Trail selection handler
  const handleTrailSelect = useCallback((trailId: string) => {
    setSelectedTrailId(trailId);
    setCurrentPage(1);
    updateFilters({
      trail_id: trailId,
      course_id: undefined, // Reset course when trail changes
    });
  }, [updateFilters]);

  // Scroll to trail selector
  const scrollToTrailSelector = useCallback(() => {
    trailSelectorRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }, []);

  // Fetch courses for selected trail
  const { courses } = useCoursesForTrail({
    trailId: selectedTrailId,
    enabled: !!selectedTrailId,
  });

  const accommodationTypes = useMemo(
    () => ACCOMMODATION_TYPES,
    []
  );

  // Handle filter changes
  const handleFiltersChange = useCallback(
    (newFilters: Partial<AccommodationFiltersType>) => {
      setCurrentPage(1);
      updateFilters(newFilters);
    },
    [updateFilters]
  );

  // Calculate pagination
  const totalPages = useMemo(() => Math.ceil(total / itemsPerPage), [total, itemsPerPage]);

  // Handle page changes with smooth scroll
  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
    goToPage(page); // Update URL with new page number
    // Scroll to results section with smooth behavior
    const resultsSection = document.querySelector('[data-testid="accommodation-results"]');
    if (resultsSection) {
      resultsSection.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [goToPage]);

  const handlePrevPage = useCallback(() => {
    if (currentPage > 1) {
      handlePageChange(currentPage - 1);
    }
  }, [currentPage, handlePageChange]);

  const handleNextPage = useCallback(() => {
    if (currentPage < totalPages) {
      handlePageChange(currentPage + 1);
    }
  }, [currentPage, totalPages, handlePageChange]);

  // Reset filters (keep trail_id)
  const handleResetFilters = useCallback(() => {
    setCurrentPage(1);
    updateFilters({
      course_id: undefined,
      distance_from_course_km: 10,
      accommodation_type: undefined,
      accommodation_type_id: undefined,
      priceFrom: undefined,
      priceTo: undefined,
    });
  }, [updateFilters]);

  // Count active filters
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.course_id) count++;
    if (filters.distance_from_course_km && filters.distance_from_course_km !== 10) count++;
    if (filters.accommodation_type) count++;
    if (filters.priceFrom) count++;
    if (filters.priceTo) count++;
    return count;
  }, [filters]);

  // Filter content component (reusable)
  const FilterContent = ({
    showApplyButton = false,
    onApply = () => {},
  }: {
    showApplyButton?: boolean;
    onApply?: () => void;
  }) => (
    <div className="space-y-6">
      <AccommodationFilters
        filters={filters}
        selectedTrailId={selectedTrailId}
        courses={courses}
        types={accommodationTypes}
        onFiltersChange={handleFiltersChange}
        onChangeTrail={scrollToTrailSelector}
        isLoading={isLoading}
      />

      {showApplyButton && (
        <Button className="w-full" onClick={onApply}>
          필터 적용
        </Button>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 pb-mobile-nav">
      <Header />

      {/* Page Header */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 py-8">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
            전국 둘레길 주변 숙소
          </h1>
          <p className="text-lg text-gray-600">
            해파랑길, 남파랑길, 서해랑길, DMZ 평화의 길 주변의 편안하고 안전한 숙박 시설을 찾아보세요
          </p>
        </div>
      </div>

      {/* Trail Selector */}
      <div ref={trailSelectorRef}>
        <TrailSelector
          selectedTrailId={selectedTrailId}
          onTrailSelect={handleTrailSelect}
        />
      </div>

      {/* Search and Filter Section */}
      <div className="bg-white py-8 border-t">
        <div className="container mx-auto px-4">
          {/* Main Content Area with Responsive Layout */}
          <div className="relative">
            {/* Desktop Layout (>= 1024px) */}
            <div className="hidden lg:grid lg:grid-cols-[280px_1fr] gap-8">
              {/* Sticky Sidebar Filters */}
              <aside className="sticky top-20 h-fit">
                <div
                  className="bg-white rounded-xl border border-gray-200 p-6 max-h-[calc(100vh-10rem)] overflow-y-auto"
                  style={{
                    scrollbarWidth: "thin",
                    scrollbarColor: "#CBD5E0 #F7FAFC",
                  }}
                >
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-lg font-semibold text-gray-900">필터</h3>
                    {activeFilterCount > 0 && (
                      <Badge variant="secondary" className="text-[#F39800] bg-[#F39800]/10">
                        {activeFilterCount}개 적용
                      </Badge>
                    )}
                  </div>

                  <FilterContent />

                  {activeFilterCount > 0 && (
                    <div className="mt-6 pt-6 border-t">
                      <Button
                        variant="outline"
                        className="w-full"
                        onClick={handleResetFilters}
                      >
                        <X className="w-4 h-4 mr-2" />
                        필터 초기화
                      </Button>
                    </div>
                  )}
                </div>
              </aside>

              {/* Main Content */}
              <div>
                <div className="flex justify-between items-center mb-6">
                  <div className="text-sm text-gray-600">
                    총 <span className="font-semibold text-[#F39800]">{total}</span>
                    개의 숙소가 있습니다
                  </div>
                </div>

                {/* Accommodation List */}
                <div data-testid="accommodation-results">
                  {isLoading ? (
                    <div className="flex justify-center items-center py-16">
                      <Loader2 className="w-6 h-6 animate-spin text-[#F39800]" />
                      <span className="ml-2 text-gray-600">숙소 정보를 불러오는 중...</span>
                    </div>
                  ) : error ? (
                    <div className="text-center py-12">
                      <p className="text-red-600 mb-2">숙소 정보를 불러오는 데 실패했습니다.</p>
                      <p className="text-gray-600">{error.message}</p>
                    </div>
                  ) : accommodations.length > 0 ? (
                    <>
                      <AccommodationList
                        accommodations={accommodations}
                        isLoading={isLoading}
                      />

                      {/* Pagination */}
                      {totalPages > 1 && (
                        <PaginationControls
                          currentPage={currentPage}
                          totalPages={totalPages}
                          itemsPerPage={itemsPerPage}
                          total={total}
                          onPageChange={handlePageChange}
                        />
                      )}
                    </>
                  ) : (
                    <div className="text-center py-16">
                      <p className="text-xl text-gray-500 mb-4">검색 조건에 맞는 숙소가 없습니다</p>
                      <p className="text-gray-400 mb-4">다른 조건으로 다시 검색해보세요</p>
                      <button
                        type="button"
                        onClick={handleResetFilters}
                        className="px-4 py-2 bg-[#F39800] text-white rounded-lg hover:bg-[#E08700] transition-colors"
                      >
                        필터 초기화
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Tablet Layout (768px - 1024px) */}
            <div className="hidden md:block lg:hidden">
              {/* Collapsible Horizontal Filters */}
              <div className="bg-gray-50 rounded-xl p-4 mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-gray-900">필터</h3>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setTabletFiltersCollapsed(!tabletFiltersCollapsed)}
                  >
                    {tabletFiltersCollapsed ? "필터 보기" : "필터 숨기기"}
                    <ChevronDown
                      className={`w-4 h-4 ml-2 transition-transform ${
                        tabletFiltersCollapsed ? "" : "rotate-180"
                      }`}
                    />
                  </Button>
                </div>

                {!tabletFiltersCollapsed && (
                  <>
                    <FilterContent />
                    <div className="flex gap-2 mt-4">
                      {activeFilterCount > 0 && (
                        <Button
                          variant="outline"
                          onClick={handleResetFilters}
                          className="flex-1"
                        >
                          <X className="w-4 h-4 mr-2" />
                          필터 초기화
                        </Button>
                      )}
                    </div>
                  </>
                )}
              </div>

              <div className="flex justify-between items-center mb-6">
                <div className="text-sm text-gray-600">
                  총 <span className="font-semibold text-[#F39800]">{total}</span>개
                </div>
                {activeFilterCount > 0 && (
                  <Badge variant="secondary" className="text-[#F39800] bg-[#F39800]/10">
                    {activeFilterCount}개 필터 적용
                  </Badge>
                )}
              </div>

              {/* Accommodation List */}
              <div data-testid="accommodation-results">
                {isLoading ? (
                  <div className="flex justify-center items-center py-16">
                    <Loader2 className="w-6 h-6 animate-spin text-[#F39800]" />
                    <span className="ml-2 text-gray-600">숙소 정보를 불러오는 중...</span>
                  </div>
                ) : error ? (
                  <div className="text-center py-12">
                    <p className="text-red-600 mb-2">숙소 정보를 불러오는 데 실패했습니다.</p>
                    <p className="text-gray-600">{error.message}</p>
                  </div>
                ) : accommodations.length > 0 ? (
                  <>
                    <AccommodationList
                      accommodations={accommodations}
                      isLoading={isLoading}
                    />

                    {/* Pagination */}
                    {totalPages > 1 && (
                      <PaginationControls
                        currentPage={currentPage}
                        totalPages={totalPages}
                        itemsPerPage={itemsPerPage}
                        total={total}
                        onPageChange={handlePageChange}
                      />
                    )}
                  </>
                ) : (
                  <div className="text-center py-16">
                    <p className="text-xl text-gray-500 mb-4">검색 조건에 맞는 숙소가 없습니다</p>
                    <p className="text-gray-400 mb-4">다른 조건으로 다시 검색해보세요</p>
                    <button
                      type="button"
                      onClick={handleResetFilters}
                      className="px-4 py-2 bg-[#F39800] text-white rounded-lg hover:bg-[#E08700] transition-colors"
                    >
                      필터 초기화
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Mobile Layout (< 768px) */}
            <div className="block md:hidden">
              <div className="flex justify-between items-center mb-6">
                <div className="text-sm text-gray-600">
                  총 <span className="font-semibold text-[#F39800]">{total}</span>개
                </div>
              </div>

              {/* Accommodation List */}
              <div data-testid="accommodation-results">
                {isLoading ? (
                  <div className="flex justify-center items-center py-16">
                    <Loader2 className="w-6 h-6 animate-spin text-[#F39800]" />
                    <span className="ml-2 text-gray-600">숙소 정보를 불러오는 중...</span>
                  </div>
                ) : error ? (
                  <div className="text-center py-12">
                    <p className="text-red-600 mb-2">숙소 정보를 불러오는 데 실패했습니다.</p>
                    <p className="text-gray-600">{error.message}</p>
                  </div>
                ) : accommodations.length > 0 ? (
                  <>
                    <AccommodationList
                      accommodations={accommodations}
                      isLoading={isLoading}
                    />

                    {/* Pagination */}
                    {totalPages > 1 && (
                      <PaginationControls
                        currentPage={currentPage}
                        totalPages={totalPages}
                        itemsPerPage={itemsPerPage}
                        total={total}
                        onPageChange={handlePageChange}
                      />
                    )}
                  </>
                ) : (
                  <div className="text-center py-16">
                    <p className="text-xl text-gray-500 mb-4">검색 조건에 맞는 숙소가 없습니다</p>
                    <p className="text-gray-400 mb-4">다른 조건으로 다시 검색해보세요</p>
                    <button
                      type="button"
                      onClick={handleResetFilters}
                      className="px-4 py-2 bg-[#F39800] text-white rounded-lg hover:bg-[#E08700] transition-colors"
                    >
                      필터 초기화
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Filter Button and Sheet */}
      <div className="md:hidden">
        {/* Fixed Filter Button */}
        <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 z-40">
          <Sheet open={mobileFilterOpen} onOpenChange={setMobileFilterOpen}>
            <SheetTrigger asChild>
              <Button size="lg" className="shadow-lg rounded-full px-6">
                <Filter className="w-5 h-5 mr-2" />
                필터
                {activeFilterCount > 0 && (
                  <Badge variant="secondary" className="ml-2 bg-white text-[#F39800]">
                    {activeFilterCount}
                  </Badge>
                )}
              </Button>
            </SheetTrigger>
            <SheetContent side="bottom" className="h-[80vh]">
              <SheetHeader>
                <SheetTitle>필터</SheetTitle>
                <SheetDescription>
                  원하는 조건을 선택하여 숙소를 찾아보세요
                </SheetDescription>
              </SheetHeader>
              <div className="mt-6 overflow-y-auto h-[calc(100%-8rem)]">
                <FilterContent showApplyButton={true} onApply={() => setMobileFilterOpen(false)} />
              </div>
              {activeFilterCount > 0 && (
                <SheetFooter className="absolute bottom-0 left-0 right-0 p-6 bg-white border-t">
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => {
                      handleResetFilters();
                      setMobileFilterOpen(false);
                    }}
                  >
                    <X className="w-4 h-4 mr-2" />
                    필터 초기화
                  </Button>
                </SheetFooter>
              )}
            </SheetContent>
          </Sheet>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default AccommodationSearch;
